// JRJ shared tune-cart module.
// Single source of truth for variant resolution, pricing, forced-induction
// add-on lookup, and the atomic cart-add call — used by BOTH the homepage
// Guided Tune Builder and the product-page tune builder (buy-buttons.liquid),
// so the two stop maintaining separate copies of this logic and cannot drift
// apart again. Landing-page mini builder can migrate to this too.
//
// Nothing here reads page-specific DOM elements — callers pass in whatever
// context they have (an engine string for variant-title matching, etc).
window.JRJ_TUNE_CART = (function () {
  'use strict';

  var catalogReadCache = Object.create(null);
  var CATALOG_READ_TTL_MS = 30000;

  // Real, distinct Shopify products — one per blower, each its own full
  // remote-tune add-on service (not a single generic $59.99 hardware fee).
  // Single source of truth for which handle/fallback price backs each
  // FORCED_INDUCTION_BLOWER_OPTIONS entry below.
  var FORCED_INDUCTION_ADDON_HANDLES = {
    'LSA Blower': 'tune-add-on-lsa-blower',
    'Hellcat Blower': 'tune-add-on-hellcat-blower'
  };
  var FORCED_INDUCTION_FALLBACK_PRICE_CENTS = {
    'LSA Blower': 25499,
    'Hellcat Blower': 25499
  };

  // Configurable in one place. Must match the amount configured on the
  // Shopify Automatic Discount described in the builder setup notes — this
  // constant only ever controls the on-page ESTIMATE text, never a real
  // cart discount (Shopify applies the real one at checkout).
  var CAM_BLOWER_BUNDLE_DISCOUNT = 50.00;

  // The only two forced-induction options actually offered, regardless of
  // make — shown together so the customer (who knows their own vehicle)
  // picks the one that applies. Single source of truth for both the
  // homepage builder and the product-page builder.
  var FORCED_INDUCTION_BLOWER_OPTIONS = ['LSA Blower', 'Hellcat Blower'];
  var GM_BOOST_BRANDS = FORCED_INDUCTION_BLOWER_OPTIONS;
  var MOPAR_BOOST_BRANDS = FORCED_INDUCTION_BLOWER_OPTIONS;
  var MOPAR_MAKES = ['RAM', 'Dodge', 'Chrysler', 'Jeep'];

  function isMoparMake(make) {
    return MOPAR_MAKES.indexOf(make) !== -1;
  }

  function boostBrandsForMake(make) {
    return isMoparMake(make) ? MOPAR_BOOST_BRANDS : GM_BOOST_BRANDS;
  }

  function fetchWithTimeout(url, options, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, timeoutMs || 6000);
    var opts = Object.assign({}, options || {});
    if (controller) opts.signal = controller.signal;
    return fetch(url, opts).finally(function () { clearTimeout(timer); });
  }

  function activeCurrency() {
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    return currency ? String(currency).toUpperCase() : 'USD';
  }

  // Share only short-lived public catalog reads. Builders often request the
  // same product for the service card, breakdown and selected add-ons at once.
  // Cart reads/writes never use this cache. Keep engine selection below the
  // cache so callers sharing a product can still resolve different variants.
  function readCatalogJSON(url, errorMessage, validate) {
    var origin = window.location && window.location.origin || '';
    var key = origin + '|' + activeCurrency() + '|' + url;
    var cached = catalogReadCache[key];
    if (cached && cached.promise) return cached.promise;
    if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.value);

    var entry = { promise: null, value: null, expiresAt: 0 };
    catalogReadCache[key] = entry;
    entry.promise = fetchWithTimeout(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }, 6000)
      .then(function (res) {
        if (!res.ok) throw new Error(errorMessage);
        return res.json();
      })
      .then(function (value) {
        if (validate) validate(value);
        entry.value = value;
        entry.expiresAt = Date.now() + CATALOG_READ_TTL_MS;
        entry.promise = null;
        return value;
      })
      .catch(function (error) {
        if (catalogReadCache[key] === entry) delete catalogReadCache[key];
        throw error;
      });
    return entry.promise;
  }

  function invalidateCatalogCache() {
    catalogReadCache = Object.create(null);
  }

  function formatPrice(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return '';
    var currency = activeCurrency();
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency
      }).format(n / 100);
    } catch (error) {
      return (currency === 'USD' ? '$' : currency + ' ') + (n / 100).toFixed(2);
    }
  }

  // Public Shopify line-item property contract consumed by the JRJ Tune
  // Portal paid-order webhook. Keep these labels stable: changing a label is
  // a portal data-migration concern, not a storefront copy change.
  var PORTAL_PURCHASE_PROPERTY_NAMES = [
    'Year',
    'Make',
    'Model',
    'Engine',
    'Transmission',
    'Drivetrain',
    'Cam',
    'Tune Type',
    'Mods',
    'LSA Blower',
    'Hellcat Blower',
    'VIN',
    'VIN Details'
  ];

  var REQUIRED_PORTAL_SELECTION_NAMES = [
    'Year',
    'Make',
    'Model',
    'Engine',
    'Cam',
    'Tune Type'
  ];

  function cleanPropertyValue(value) {
    if (Array.isArray(value)) {
      return value.map(function (entry) { return String(entry == null ? '' : entry).trim(); })
        .filter(Boolean)
        .join(', ');
    }
    return String(value == null ? '' : value).trim();
  }

  function addSelectedProperty(properties, name, value) {
    var clean = cleanPropertyValue(value);
    if (clean) properties[name] = clean;
  }

  function normalizeModsValue(value, downloadable) {
    if (downloadable) return 'Not Applicable — Downloadable .HPT (no modification support)';
    var mods = Array.isArray(value) ? value.slice() : cleanPropertyValue(value).split(/[;,]/);
    mods = mods.map(cleanPropertyValue).filter(Boolean).filter(function (entry) {
      return entry !== 'LSA Blower' && entry !== 'Hellcat Blower';
    });
    mods = mods.filter(function (entry) { return !/^none selected$/i.test(entry); });
    var hasLongTubes = mods.some(function (entry) { return /long[ -]?tube/i.test(entry); });
    var hasHeaderChoice = mods.some(function (entry) { return /stock \/ short[ -]?tube headers/i.test(entry); });
    if (!mods.length) return 'None Selected; Stock / Short-Tube Headers';
    if (!hasLongTubes && !hasHeaderChoice) mods.unshift('Stock / Short-Tube Headers');
    return mods.filter(function (entry, index) { return mods.indexOf(entry) === index; }).join('; ');
  }

  function buildPortalPurchaseProperties(snapshot) {
    var source = snapshot || {};
    var tuneType = cleanPropertyValue(source.tuneType);
    var downloadable = source.downloadable === true || /downloadable|download/i.test(tuneType);
    var properties = {};
    properties.Year = cleanPropertyValue(source.year);
    properties.Make = cleanPropertyValue(source.make);
    properties.Model = cleanPropertyValue(source.model);
    properties.Engine = cleanPropertyValue(source.engine);
    properties.Transmission = cleanPropertyValue(source.transmission) || 'Not Provided';
    // Drivetrain selections intentionally contain the complete drivetrain and
    // transfer-case description. Preserve the selected Shopify value exactly;
    // do not reduce values such as "Auto 4WD — NQH 2-Speed AutoTrac" to "4WD".
    var selectedDrivetrain = source.drivetrain == null ? '' : String(source.drivetrain);
    properties.Drivetrain = selectedDrivetrain || 'Not Provided';
    properties.Cam = cleanPropertyValue(source.cam);
    properties['Tune Type'] = tuneType;
    properties.Mods = normalizeModsValue(source.mods, downloadable);

    // These two checkboxes are always present on supported tune-builder paths.
    // Explicit Yes/No values give the webhook an unambiguous locked record;
    // downloadable paths clear both checkboxes before this snapshot is built.
    properties['LSA Blower'] = source.lsaBlower === true ? 'Yes' : 'No';
    properties['Hellcat Blower'] = source.hellcatBlower === true ? 'Yes' : 'No';

    properties.VIN = cleanPropertyValue(source.vin) || 'Not Provided';
    properties['VIN Details'] = cleanPropertyValue(source.vinDetails) || 'Not Provided';
    return properties;
  }

  function validatePortalPurchaseProperties(properties) {
    var source = properties || {};
    var errors = [];
    PORTAL_PURCHASE_PROPERTY_NAMES.forEach(function (name) {
      if (!Object.prototype.hasOwnProperty.call(source, name) || !cleanPropertyValue(source[name])) {
        errors.push('Missing property: ' + name);
      }
    });
    REQUIRED_PORTAL_SELECTION_NAMES.forEach(function (name) {
      var value = cleanPropertyValue(source[name]);
      if (!value || /^(not provided|not applicable|none selected)$/i.test(value)) errors.push('Required selection missing: ' + name);
    });
    ['LSA Blower', 'Hellcat Blower'].forEach(function (name) {
      if (['Yes', 'No'].indexOf(cleanPropertyValue(source[name])) === -1) errors.push('Invalid explicit blower value: ' + name);
    });
    if (/downloadable|download/i.test(cleanPropertyValue(source['Tune Type'])) &&
        cleanPropertyValue(source.Mods).indexOf('Not Applicable — Downloadable .HPT') !== 0) {
      errors.push('Downloadable .HPT products cannot carry modification selections.');
    }
    return { ok: errors.length === 0, errors: errors };
  }

  function redactVin(value) {
    var clean = cleanPropertyValue(value);
    if (!clean || clean === 'Not Provided') return clean || 'Not Provided';
    return '***' + clean.slice(-4);
  }

  function safeDiagnosticItems(items) {
    return (items || []).map(function (item) {
      var properties = Object.assign({}, item.properties || {});
      if (properties.VIN) properties.VIN = redactVin(properties.VIN);
      if (properties['VIN Details'] && properties['VIN Details'] !== 'Not Provided') properties['VIN Details'] = '[provided]';
      return { id: item.id, quantity: item.quantity, properties: properties };
    });
  }

  function primaryTuneItems(items) {
    return (items || []).filter(function (item) {
      return item && item.properties && String(item.properties['_jrj_primary_tune']) === 'true';
    });
  }

  function validateRequestedCartItems(items) {
    var primaries = primaryTuneItems(items);
    var errors = [];
    if (!primaries.length) errors.push('No canonical primary tune line was supplied.');
    primaries.forEach(function (item, index) {
      var result = validatePortalPurchaseProperties(item.properties);
      result.errors.forEach(function (message) { errors.push('Primary tune ' + (index + 1) + ': ' + message); });
      if (!cleanPropertyValue(item.properties['_linked_tune_key'])) errors.push('Primary tune ' + (index + 1) + ': missing _linked_tune_key.');
    });
    return { ok: errors.length === 0, errors: errors };
  }

  function returnedItems(payload) {
    if (payload && Array.isArray(payload.items)) return payload.items;
    if (payload && payload.properties) return [payload];
    return [];
  }

  function matchingReturnedPrimary(requested, returned) {
    var requestedKey = cleanPropertyValue(requested.properties && requested.properties['_linked_tune_key']);
    return returned.find(function (item) {
      var itemKey = cleanPropertyValue(item && item.properties && item.properties['_linked_tune_key']);
      return requestedKey && itemKey === requestedKey && String(item.variant_id || item.id) === String(requested.id);
    });
  }

  function verifyReturnedCartItems(payload, requestedItems) {
    var returned = returnedItems(payload);
    var errors = [];
    primaryTuneItems(requestedItems).forEach(function (requested, index) {
      var actual = matchingReturnedPrimary(requested, returned);
      if (!actual) {
        errors.push('Primary tune ' + (index + 1) + ' was not returned with its linked build key.');
        return;
      }
      var remotePropertyNames = Object.keys(requested.properties || {}).filter(function (name) { return name.indexOf('_JRJ Remote ') === 0; });
      PORTAL_PURCHASE_PROPERTY_NAMES.concat(['_JRJ Project Type', '_linked_tune_key', '_jrj_primary_tune'], remotePropertyNames).forEach(function (name) {
        if (!Object.prototype.hasOwnProperty.call(requested.properties || {}, name)) return;
        var matches = name.indexOf('_JRJ Remote ') === 0
          ? actual.properties && actual.properties[name] === requested.properties[name]
          : cleanPropertyValue(actual.properties && actual.properties[name]) === cleanPropertyValue(requested.properties[name]);
        if (!matches) {
          errors.push('Primary tune ' + (index + 1) + ' property mismatch: ' + name);
        }
      });
    });
    return { ok: errors.length === 0, errors: errors, items: returned };
  }

  function rollbackReturnedItems(payload) {
    var lines = returnedItems(payload).filter(function (item) { return item && (item.key || item.id || item.variant_id); });
    return Promise.all(lines.map(function (item) {
      return fetchWithTimeout('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key || item.id || item.variant_id, quantity: 0 })
      }, 6000).catch(function () { return null; });
    }));
  }

  function rollbackByLinkedTuneKeys(items) {
    var keys = primaryTuneItems(items).map(function (item) {
      return cleanPropertyValue(item.properties && item.properties['_linked_tune_key']);
    }).filter(Boolean);
    if (!keys.length) return Promise.resolve();
    return fetchWithTimeout('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }, 6000)
      .then(function (response) { return response.ok ? response.json() : { items: [] }; })
      .then(function (cartData) {
        var matches = (cartData.items || []).filter(function (item) {
          return keys.indexOf(cleanPropertyValue(item.properties && item.properties['_linked_tune_key'])) !== -1;
        });
        return rollbackReturnedItems({ items: matches });
      }).catch(function () { return null; });
  }

  // A private per-build key keeps separately priced add-on products associated
  // with the tune line without copying customer-facing vehicle data onto them.
  function createLinkedTuneKey() {
    var randomPart = '';
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      var randomValues = new Uint32Array(2);
      window.crypto.getRandomValues(randomValues);
      randomPart = randomValues[0].toString(36) + randomValues[1].toString(36);
    } else {
      randomPart = Math.random().toString(36).slice(2, 14);
    }
    return 'jrj-' + Date.now().toString(36) + '-' + randomPart;
  }

  function buildLinkedTuneProperties(linkedTuneKey) {
    var key = cleanPropertyValue(linkedTuneKey);
    return key ? { '_linked_tune_key': key } : {};
  }

  function markPrimaryTuneProperties(properties, linkedTuneKey) {
    var target = properties || {};
    target['_jrj_primary_tune'] = 'true';
    Object.assign(target, buildLinkedTuneProperties(linkedTuneKey));
    return target;
  }

  function normalizeVariantOption(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  // Resolve a variant from Liquid-serialized product data by named Shopify
  // options. This intentionally never falls back to the first variant: a
  // vehicle-specific route must either find one exact available match or stop.
  function resolveConfiguredVariantByOptions(productConfig, expectedOptions) {
    var config = productConfig || {};
    var optionNames = Array.isArray(config.optionNames) ? config.optionNames : [];
    var variants = Array.isArray(config.variants) ? config.variants : [];
    var expected = expectedOptions || {};
    var expectedNames = Object.keys(expected);

    if (config.enabled === false || !expectedNames.length) {
      return { ok: false, error: 'The vehicle product route is not configured.' };
    }
    if (optionNames.length !== expectedNames.length) {
      return { ok: false, error: 'The vehicle product must contain only the required Shopify options.' };
    }

    var optionIndexes = {};
    for (var i = 0; i < expectedNames.length; i += 1) {
      var expectedName = expectedNames[i];
      var normalizedName = normalizeVariantOption(expectedName);
      var matches = [];
      optionNames.forEach(function (optionName, index) {
        if (normalizeVariantOption(optionName) === normalizedName) matches.push(index);
      });
      if (matches.length !== 1) {
        return { ok: false, error: 'The vehicle product must have exactly one "' + expectedName + '" option.' };
      }
      optionIndexes[expectedName] = matches[0];
    }

    var exactMatches = variants.filter(function (variant) {
      var values = variant && Array.isArray(variant.options) ? variant.options : [];
      return expectedNames.every(function (name) {
        return normalizeVariantOption(values[optionIndexes[name]]) === normalizeVariantOption(expected[name]);
      });
    });

    if (exactMatches.length !== 1) {
      return { ok: false, error: 'No single exact vehicle year and tune type variant is configured.' };
    }

    var exactVariant = exactMatches[0];
    var variantId = Number(exactVariant.id);
    if (!isFinite(variantId) || variantId <= 0) {
      return { ok: false, error: 'The exact vehicle variant has an invalid Shopify variant ID.' };
    }
    if (exactVariant.available === false) {
      return { ok: false, error: 'The exact vehicle year and tune type variant is unavailable.' };
    }
    if (exactVariant.requiresShipping === true) {
      return { ok: false, error: 'The vehicle tuning service variant cannot require shipping.' };
    }

    return {
      ok: true,
      variant: exactVariant,
      item: {
        variantId: variantId,
        title: config.title || 'Vehicle-Specific Tune',
        priceCents: Number(exactVariant.priceCents) || 0,
        strictVariant: true,
        expectedVariantOptions: exactVariant.options.slice()
      }
    };
  }

  // engineHint: optional string used only to prefer a same-engine variant
  // when a product has multiple variants (e.g. by displacement). Safe to omit.
  function getVariantFromHandle(handle, engineHint) {
    return readCatalogJSON('/products/' + handle + '.js', 'Could not load product: ' + handle, function (product) {
        if (!product || !Array.isArray(product.variants) || !product.variants.length ||
            !product.variants.every(function (variant) {
              return variant && /^\d+$/.test(String(variant.id || '')) && Number(variant.id) > 0 &&
                (typeof variant.price === 'number' || (typeof variant.price === 'string' && variant.price.trim() !== '')) &&
                isFinite(Number(variant.price)) && Number(variant.price) >= 0;
            })) throw new Error('Invalid product variants for: ' + handle);
      })
      .then(function (product) {
        var engine = String(engineHint || '').toLowerCase();
        var variant = product.variants.find(function (v) {
          var t = String(v.title || '').toLowerCase();
          return v.available && engine && t.indexOf(engine) !== -1;
        }) || product.variants.find(function (v) { return v.available; }) || product.variants[0];
        return { id: variant.id, product: product, variant: variant };
      });
  }

  function getVariantById(variantId) {
    return readCatalogJSON('/variants/' + variantId + '.js', 'Could not load variant: ' + variantId, function (variant) {
      if (!variant || String(variant.id) !== String(variantId) || variant.price === null || variant.price === '' ||
          !isFinite(Number(variant.price)) || Number(variant.price) < 0) {
        throw new Error('Invalid variant response: ' + variantId);
      }
    });
  }

  // item: { variantId?, handle?, title, priceCents?, forceDisplayPriceCents? }
  // engineHint: passed through to getVariantFromHandle when item has no variantId.
  function resolvePricedItem(item, engineHint) {
    if (!item) return Promise.resolve(null);
    if (item.variantId) {
      return getVariantById(item.variantId)
        .then(function (variant) {
          if (item.strictVariant) {
            if (!variant || variant.available === false) throw new Error('The exact configured variant is unavailable.');
            if (Array.isArray(item.expectedVariantOptions) && Array.isArray(variant.options)) {
              var optionsMatch = item.expectedVariantOptions.length === variant.options.length && item.expectedVariantOptions.every(function (value, index) {
                return normalizeVariantOption(value) === normalizeVariantOption(variant.options[index]);
              });
              if (!optionsMatch) throw new Error('The exact configured variant options changed in Shopify.');
            }
          }
          var forced = Number(item.forceDisplayPriceCents);
          return {
            id: item.variantId,
            title: item.title,
            priceCents: isFinite(forced) && forced > 0 ? forced : (typeof variant.price !== 'undefined' ? Number(variant.price) : Number(item.priceCents) || 0),
            variant: variant
          };
        })
        .catch(function (err) {
          if (item.strictVariant) throw err;
          console.warn('JRJ Tune Cart: live lookup failed for variant ' + item.variantId + ' (' + item.title + ') — falling back to configured price, but this ID may no longer exist in Shopify and could fail at cart/add time:', err);
          return { id: item.variantId, title: item.title, priceCents: Number(item.forceDisplayPriceCents) || Number(item.priceCents) || 0, variant: null };
        });
    }
    return getVariantFromHandle(item.handle, engineHint).then(function (resolved) {
      var forcedHandle = Number(item.forceDisplayPriceCents);
      return {
        id: resolved.id,
        title: item.title,
        priceCents: isFinite(forcedHandle) && forcedHandle > 0 ? forcedHandle : (resolved && resolved.variant && typeof resolved.variant.price !== 'undefined' ? Number(resolved.variant.price) : Number(resolved.product && resolved.product.price) || 0),
        variant: resolved.variant
      };
    });
  }

  // blowerName must be one of FORCED_INDUCTION_BLOWER_OPTIONS ('LSA Blower'
  // or 'Hellcat Blower') -- each resolves to its own distinct real product,
  // never a shared/generic add-on.
  function resolveForcedInductionAddon(blowerName) {
    var handle = FORCED_INDUCTION_ADDON_HANDLES[blowerName];
    var fallbackPriceCents = FORCED_INDUCTION_FALLBACK_PRICE_CENTS[blowerName] || 0;
    var title = 'Tune Add On: ' + blowerName;
    if (!handle) return Promise.resolve({ id: null, title: title, priceCents: fallbackPriceCents, variant: null });
    return getVariantFromHandle(handle)
      .then(function (resolved) {
        var entry = {
          id: resolved.id,
          title: title,
          priceCents: resolved.variant && typeof resolved.variant.price !== 'undefined' ? Number(resolved.variant.price) : fallbackPriceCents,
          variant: resolved.variant
        };
        return entry;
      })
      .catch(function (err) {
        console.warn('JRJ Tune Cart: forced-induction add-on product unavailable:', blowerName, err);
        return { id: null, title: title, priceCents: fallbackPriceCents, variant: null };
      });
  }

  function hasRealCamUpgrade(camUpgrade) {
    return !!(camUpgrade && (camUpgrade.variantId || camUpgrade.handle));
  }

  function bundleDiscountApplies(opts) {
    // opts: { tuneType: 'custom'|'download', aspiration: 'na'|'supercharged'|'turbocharged', camUpgrade }
    if (!opts || opts.tuneType !== 'custom') return false;
    if (!opts.aspiration || opts.aspiration === 'na') return false;
    return hasRealCamUpgrade(opts.camUpgrade);
  }

  // ---------------------------------------------------------------------
  // Platform / cam / add-on resolution.
  //
  // This is the same classification logic the homepage Guided Tune Builder
  // used to keep as its own private functions (getCamEngineKey,
  // isHemiGroup, isGmDodAfmPlatform, isVvtOnlyPlatform, getMatchedBaseProduct,
  // getSelectedCamUpgrade, getHomepageAddonVariantId). It now lives here once,
  // and both the homepage builder and the product-page builder
  // (buy-buttons.liquid) call through it, so the two cannot drift apart.
  //
  // All of it reads from window.JRJ_TUNE_BUILDER_DRAFT (the data file) --
  // this module never hardcodes a product/variant ID of its own.
  // ---------------------------------------------------------------------

  function draftData() {
    return window.JRJ_TUNE_BUILDER_DRAFT || {};
  }

  function baseProductForKey(key) {
    return (draftData().baseProducts || {})[key] || null;
  }

  // The homepage builder knows the customer's exact engine selection and can
  // find the matching group directly. A product page only knows which real
  // Shopify product it is (its handle) -- this looks that handle up against
  // baseProducts to find which platform key it belongs to.
  function findBaseProductKeyByHandle(handle) {
    var bp = draftData().baseProducts || {};
    var keys = Object.keys(bp);
    for (var i = 0; i < keys.length; i++) {
      var product = bp[keys[i]] || {};
      if (product.handle === handle || (product.legacyHandles || []).indexOf(handle) !== -1) return keys[i];
    }
    return null;
  }

  function selectionValueMatchesYear(yearValue, rangesValue) {
    if (!rangesValue) return true;
    var year = parseInt(yearValue || '', 10);
    if (!year) return true;
    var ranges = String(rangesValue).match(/\d{4}(?:\s*-\s*(?:\d{4}|Present))?/g) || [];
    return ranges.some(function (range) {
      var parts = range.split('-').map(function (part) { return part.trim(); });
      var start = parseInt(parts[0], 10);
      var end = parts[1] === 'Present' ? 2099 : parseInt(parts[1] || parts[0], 10);
      return year >= start && year <= end;
    });
  }

  function normalizedSelectionValue(value) {
    return cleanPropertyValue(value).toLowerCase();
  }

  function groupHasMake(group, makeValue) {
    var make = normalizedSelectionValue(makeValue);
    return Object.keys((group && group.makes) || {}).some(function (candidate) {
      return normalizedSelectionValue(candidate) === make;
    });
  }

  function canonicalGroupMake(group, makeValue) {
    var make = normalizedSelectionValue(makeValue);
    return Object.keys((group && group.makes) || {}).filter(function (candidate) {
      return normalizedSelectionValue(candidate) === make;
    })[0] || '';
  }

  function canonicalGroupModel(group, makeValue, modelValue) {
    var make = canonicalGroupMake(group, makeValue);
    var model = normalizedSelectionValue(modelValue);
    return (((group && group.makes) || {})[make] || []).filter(function (candidate) {
      return normalizedSelectionValue(candidate) === model;
    })[0] || '';
  }

  function modelAllowedForGroupYear(yearValue, makeValue, modelValue, group) {
    var make = canonicalGroupMake(group, makeValue);
    var model = canonicalGroupModel(group, make, modelValue);
    if (!make || !model) return false;
    var mapped = group && group.modelYears && group.modelYears[make] && group.modelYears[make][model];
    return !mapped || selectionValueMatchesYear(yearValue, mapped);
  }

  // Rules may intentionally name a vehicle family ("Dodge Charger") while
  // another rule names a specific trim ("Dodge Charger SRT Hellcat"). Use
  // the longest matching application so the specific trim wins and a broad
  // family rule cannot add the wrong engine to it.
  function engineRuleMatchScore(rule, makeValue, modelValue) {
    var make = cleanPropertyValue(makeValue);
    var model = cleanPropertyValue(modelValue);
    var full = cleanPropertyValue(make + ' ' + model);
    var candidates = [normalizedSelectionValue(full)];
    if (normalizedSelectionValue(model).indexOf(normalizedSelectionValue(make) + ' ') === 0) {
      candidates.push(normalizedSelectionValue(model));
    }
    var best = 0;
    (rule && rule.appliesTo || []).forEach(function (value) {
      var application = normalizedSelectionValue(value);
      candidates.forEach(function (candidate) {
        if (candidate.indexOf(application) === 0) {
          best = Math.max(best, application.length);
        }
      });
    });
    return best;
  }

  function engineRuleAppliesToVehicle(rule, makeValue, modelValue) {
    return engineRuleMatchScore(rule, makeValue, modelValue) > 0;
  }

  function vehicleSelectionMatches(yearValue, makeValue, modelValue, engineValue) {
    var year = parseInt(yearValue || '', 10);
    var engine = normalizedSelectionValue(engineValue);
    var matches = [];

    (draftData().groups || []).forEach(function (group) {
      if (year && !selectionValueMatchesYear(year, group.years)) return;
      if (!groupHasMake(group, makeValue)) return;
      var make = canonicalGroupMake(group, makeValue);
      var model = canonicalGroupModel(group, make, modelValue);
      if (!model || !modelAllowedForGroupYear(year, make, model, group)) return;

      var eligibleRules = (group.engineRules || []).map(function (rule) {
        return { rule: rule, score: engineRuleMatchScore(rule, make, model) };
      }).filter(function (candidate) {
        return candidate.score > 0 && selectionValueMatchesYear(year, candidate.rule.years);
      });
      var bestScore = eligibleRules.reduce(function (best, candidate) {
        return Math.max(best, candidate.score);
      }, 0);

      eligibleRules.filter(function (candidate) { return candidate.score === bestScore; }).forEach(function (candidate) {
        var rule = candidate.rule;
        if (engine && normalizedSelectionValue(rule.engine) !== engine) return;
        var key = baseProductKeyForEngine(group, rule.engine);
        matches.push({
          year: year,
          make: make,
          model: model,
          engine: rule.engine,
          group: group,
          groupId: group.id,
          baseProductKey: key,
          baseProduct: baseProductForKey(key)
        });
      });
    });

    var seen = {};
    var uniqueMatches = [];
    matches.forEach(function (match) {
      var id = [match.make, match.model, match.engine, match.baseProductKey].join('|');
      if (typeof seen[id] === 'number') {
        var prior = uniqueMatches[seen[id]];
        var priorDirect = prior.group && prior.group.baseProductKey === prior.baseProductKey;
        var nextDirect = match.group && match.group.baseProductKey === match.baseProductKey;
        if (!priorDirect && nextDirect) uniqueMatches[seen[id]] = match;
        return;
      }
      seen[id] = uniqueMatches.length;
      uniqueMatches.push(match);
    });
    return uniqueMatches;
  }

  function resolveVehicleSelection(yearValue, makeValue, modelValue, engineValue) {
    var matches = vehicleSelectionMatches(yearValue, makeValue, modelValue, engineValue);
    if (!matches.length) return null;
    var keys = matches.map(function (match) { return match.baseProductKey; }).filter(function (key, index, list) {
      return list.indexOf(key) === index;
    });
    if (keys.length !== 1) return { ambiguous: true, matches: matches };
    var selected = matches[0];
    selected.ambiguous = false;
    selected.matches = matches;
    return selected;
  }

  function vehicleYears() {
    var years = [];
    var currentYear = new Date().getFullYear();
    (draftData().groups || []).forEach(function (group) {
      var ranges = String(group.years || '').match(/\d{4}(?:\s*-\s*(?:\d{4}|Present))?/g) || [];
      ranges.forEach(function (range) {
        var parts = range.split('-').map(function (part) { return part.trim(); });
        var start = parseInt(parts[0], 10);
        var end = parts[1] === 'Present' ? currentYear : parseInt(parts[1] || parts[0], 10);
        for (var year = start; year <= end; year += 1) years.push(year);
      });
    });
    return years.filter(function (year, index, list) { return list.indexOf(year) === index; })
      .sort(function (a, b) { return b - a; });
  }

  function vehicleMakes(yearValue) {
    var makes = [];
    (draftData().groups || []).forEach(function (group) {
      if (!selectionValueMatchesYear(yearValue, group.years)) return;
      Object.keys(group.makes || {}).forEach(function (make) { makes.push(make); });
    });
    return makes.filter(function (make, index, list) { return list.indexOf(make) === index; }).sort();
  }

  function vehicleModels(yearValue, makeValue) {
    var models = [];
    (draftData().groups || []).forEach(function (group) {
      if (!selectionValueMatchesYear(yearValue, group.years) || !groupHasMake(group, makeValue)) return;
      var make = canonicalGroupMake(group, makeValue);
      (group.makes[make] || []).forEach(function (model) {
        if (!modelAllowedForGroupYear(yearValue, make, model, group)) return;
        var hasEngine = vehicleSelectionMatches(yearValue, make, model, '').length > 0;
        if (hasEngine) models.push(model);
      });
    });
    return models.filter(function (model, index, list) { return list.indexOf(model) === index; }).sort();
  }

  function vehicleEngines(yearValue, makeValue, modelValue) {
    return vehicleSelectionMatches(yearValue, makeValue, modelValue, '').map(function (match) { return match.engine; })
      .filter(function (engine, index, list) { return list.indexOf(engine) === index; }).sort();
  }

  function configuredProductPriceCents(product) {
    if (!product) return 0;
    var explicitCents = Number(product.priceCents);
    if (isFinite(explicitCents) && explicitCents > 0) return Math.round(explicitCents);
    var dollars = Number(product.price);
    return isFinite(dollars) && dollars > 0 ? Math.round(dollars * 100) : 0;
  }

  function customTuneProductsForGroups(groups, engineHint) {
    var products = [];
    var seenKeys = {};
    (groups || []).forEach(function (group) {
      var key = String(baseProductKeyForEngine(group, engineHint) || '');
      var product = key ? baseProductForKey(key) : null;
      if (!product || seenKeys[key]) return;
      seenKeys[key] = true;
      products.push(product);
    });
    return products;
  }

  function customTunePriceRangeFromProducts(products) {
    var prices = (products || []).map(configuredProductPriceCents).filter(function (price) { return price > 0; });
    if (!prices.length) return null;
    return {
      minCents: Math.min.apply(Math, prices),
      maxCents: Math.max.apply(Math, prices)
    };
  }

  function configuredCustomTunePriceRange(groups, engineHint) {
    // The static map is denominated in USD. It is never a presentment quote.
    if (activeCurrency() !== 'USD') return null;
    return customTunePriceRangeFromProducts(customTuneProductsForGroups(groups, engineHint));
  }

  function resolveCustomTunePriceRange(groups, engineHint) {
    var products = customTuneProductsForGroups(groups, engineHint);
    if (!products.length) return Promise.resolve(null);
    return Promise.all(products.map(function (product) {
      var item = {
        handle: product.handle,
        title: product.title,
        priceCents: configuredProductPriceCents(product)
      };
      return resolvePricedItem(item, engineHint).then(function (resolved) {
        // Shopify owns presentment pricing and rounding. Never substitute the
        // USD map if a catalog request fails or a product is unavailable.
        if (!resolved || !resolved.variant || resolved.variant.available !== true ||
            !isFinite(Number(resolved.priceCents)) || Number(resolved.priceCents) <= 0) {
          throw new Error('The custom tune starting price could not be verified.');
        }
        return Number(resolved.priceCents);
      });
    })).then(function (prices) {
      prices = prices.filter(function (price) { return Number(price) > 0; });
      if (!prices.length) return null;
      return {
        minCents: Math.min.apply(Math, prices),
        maxCents: Math.max.apply(Math, prices)
      };
    });
  }

  function formatStartingPriceRange(range) {
    if (!range || !range.minCents) return 'Price Unavailable';
    if (range.minCents === range.maxCents) return 'Starting at ' + formatPrice(range.minCents);
    return formatPrice(range.minCents) + '-' + formatPrice(range.maxCents) + ' depending on engine/platform';
  }

  function isGenVTruckKey(baseProductKey) {
    return baseProductKey === 'gen5_gm_truck_suv';
  }

  function isRecognizedGmMake(makeValue) {
    var make = cleanPropertyValue(makeValue).toLowerCase();
    if (!make) return false;
    return /(^|\b)(chevrolet|chevy|gmc|cadillac|pontiac|buick|holden|hsv|hummer|saturn|oldsmobile|isuzu|saab|gm)(\b|$)/i.test(make);
  }

  function isGmAddOnContext(baseProductKey, makeValue) {
    if (/^gen[345]_gm/i.test(String(baseProductKey || ''))) return true;
    return isRecognizedGmMake(makeValue);
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function engineCodePresent(engineValue, code) {
    return new RegExp('(^|[^A-Z0-9])' + escapeRegExp(code) + '([^A-Z0-9]|$)', 'i').test(cleanPropertyValue(engineValue));
  }

  function gmApplicationClass(group) {
    var value = String((group && (group.baseProductKey || group.id || group.label)) || '');
    return /_car\b|\bcar\b/i.test(value) ? 'car' : 'truck_suv';
  }

  function engineApplicationFor(group, engineValue) {
    var data = draftData().engineApplications || {};
    var engine = cleanPropertyValue(engineValue);
    var baseKey = String((group && group.baseProductKey) || '');

    if (isHemiGroup(group) || /^hemi/i.test(baseKey)) {
      var hemiRules = data.hemi || {};
      var hemiKeys = Object.keys(hemiRules);
      for (var h = 0; h < hemiKeys.length; h += 1) {
        var hemiRule = hemiRules[hemiKeys[h]] || {};
        if (hemiRule.match && new RegExp(hemiRule.match, 'i').test(engine)) {
          return {
            key: hemiRule.key || hemiKeys[h],
            architecture: 'hemi',
            applicationClass: /_car_/.test(baseKey) ? 'car' : 'truck_suv',
            baseProductKey: baseKey,
            modFamily: hemiRule.modFamily || 'standard',
            camOptionsKey: hemiRule.camOptionsKey || ''
          };
        }
      }
      return {
        key: 'hemi_unknown',
        architecture: 'hemi',
        applicationClass: /_car_/.test(baseKey) ? 'car' : 'truck_suv',
        baseProductKey: baseKey,
        modFamily: 'standard',
        camOptionsKey: ''
      };
    }

    var gmRules = data.gm || {};
    var generations = ['gen3', 'gen4', 'gen5'];
    for (var i = 0; i < generations.length; i += 1) {
      var generation = generations[i];
      var gmRule = gmRules[generation] || {};
      var codes = gmRule.codes || [];
      for (var c = 0; c < codes.length; c += 1) {
        if (!engineCodePresent(engine, codes[c])) continue;
        var vehicleClass = gmApplicationClass(group);
        return {
          key: gmRule.key || ('gm_' + generation),
          architecture: generation,
          applicationClass: vehicleClass,
          baseProductKey: generation + '_gm_' + vehicleClass,
          modFamily: gmRule.modFamily || (generation === 'gen5' ? 'gen_v' : 'standard'),
          engineCode: codes[c]
        };
      }
    }

    return {
      key: 'unknown',
      architecture: '',
      applicationClass: gmApplicationClass(group),
      baseProductKey: baseKey,
      modFamily: /^gen5_gm/i.test(baseKey) ? 'gen_v' : 'standard'
    };
  }

  function baseProductKeyForEngine(group, engineValue) {
    var application = engineApplicationFor(group, engineValue);
    return application.baseProductKey || String((group && group.baseProductKey) || '');
  }

  // GEN V add-ons follow the selected GM engine architecture. Year is kept in
  // the signature for backwards compatibility but never determines routing.
  function usesGenVAddOnProducts(baseProductKey, yearValue, makeValue, engineValue) {
    var application = engineApplicationFor({ id: baseProductKey, baseProductKey: baseProductKey }, engineValue);
    if (cleanPropertyValue(engineValue)) return application.key === 'gm_gen5';
    return /^gen5_gm/i.test(String(baseProductKey || ''));
  }

  function addonProductConfig(name, baseProductKey, yearValue, makeValue, engineValue) {
    var homepage = draftData().homepage || {};
    var general = (homepage.addonProducts || {})[name] || null;
    var genV = (homepage.genVAddonProducts || {})[name] || null;
    if (usesGenVAddOnProducts(baseProductKey, yearValue, makeValue, engineValue) && genV) return genV;
    return general;
  }

  function resolveAddonProduct(name, baseProductKey, yearValue, makeValue, engineHint) {
    var item = addonProductConfig(name, baseProductKey, yearValue, makeValue, engineHint);
    if (!item || !item.handle) return Promise.resolve(null);
    return resolvePricedItem(item, engineHint).catch(function (err) {
      console.warn('JRJ Tune Cart: add-on product unavailable for', name, item.handle, err);
      return { id: null, title: item.title || name, priceCents: Number(item.priceCents) || 0, variant: null, handle: item.handle };
    });
  }

  function isMoparBaseProductKey(baseProductKey) {
    return String(baseProductKey || '').indexOf('hemi') === 0;
  }

  // Maps each real base-tune product to the camOptions list key used in the
  // shared data file. HEMI product pages refine this platform fallback as
  // soon as the customer enters a recognizable 5.7/6.1/6.4/6.2 engine.
  var CAM_OPTIONS_KEY_BY_BASE_PRODUCT_KEY = {
    gen3_gm_truck_suv: 'GM Safe Fallback',
    gen3_gm_car: 'GM Safe Fallback',
    gen4_gm_truck_suv: 'GM Safe Fallback',
    gen4_gm_car: 'GM Safe Fallback',
    gen5_gm_truck_suv: 'GM Safe Fallback',
    gen5_gm_car: 'GM Safe Fallback',
    hemi_ram_2009_2012: 'Dodge RAM HEMI',
    hemi_car_2009_2014: 'Dodge RAM HEMI',
    hemi_ram_2013_2018: 'Dodge RAM HEMI',
    hemi_car_2015_2023: 'Dodge RAM HEMI',
    hemi_ram_2019_2022: 'Dodge RAM HEMI'
  };

  // Same platform-level-precision trade-off as above: which base-tune
  // products are eligible for the "Stock Cam / Delete Tune Only" and
  // "VVT Delete Only" substitutions, and which delete product each maps to.
  var MDS_DELETE_BASE_KEYS = ['hemi_ram_2009_2012', 'hemi_car_2009_2014', 'hemi_ram_2013_2018', 'hemi_car_2015_2023', 'hemi_ram_2019_2022'];
  var DOD_AFM_DELETE_BASE_KEYS = ['gen4_gm_truck_suv', 'gen5_gm_truck_suv', 'gen5_gm_car'];
  var VVT_ONLY_BASE_KEYS = ['gen5_gm_truck_suv', 'gen5_gm_car'];

  function camOptionsKeyForBaseProductKey(baseProductKey) {
    return CAM_OPTIONS_KEY_BY_BASE_PRODUCT_KEY[baseProductKey] || 'Other';
  }

  function hemiCamOptionsKey(engineValue) {
    var engine = cleanPropertyValue(engineValue);
    if (/(^|[^0-9])5\.7([^0-9]|$)/.test(engine)) return 'HEMI 5.7 VVT';
    if (/(^|[^0-9])6\.1([^0-9]|$)/.test(engine)) return 'HEMI 6.1 NON-VVT';
    if (/\bBGE\b/i.test(engine)) return 'HEMI 6.4 HD BGE';
    if (/(^|[^0-9])6\.4([^0-9]|$)|(^|[^0-9])392([^0-9]|$)/.test(engine)) return 'HEMI 6.4 VVT';
    if (/(^|[^0-9])6\.2([^0-9]|$)|hellcat|trackhawk|demon|redeye|trx/i.test(engine)) return 'HEMI 6.2 HELLCAT';
    return '';
  }

  function gmCamOptionsKey(baseProductKey, engineValue) {
    var baseKey = String(baseProductKey || '');
    var engine = cleanPropertyValue(engineValue);
    if (!/^gen[345]_gm/i.test(baseKey) || !engine) return '';

    if (/gen3_gm_truck_suv/i.test(baseKey)) return 'GM LS Truck Cathedral';
    if (/gen3_gm_car/i.test(baseKey)) return 'GM LS Car Cathedral';

    if (/gen4_gm_truck_suv/i.test(baseKey)) {
      if (/\b(?:LY6|L76|LFA|LZ1|L92|L9H|L94|L96|LC8)\b/i.test(engine)) return 'GM LS Truck Rectangle';
      return 'GM LS Truck Cathedral';
    }

    if (/gen4_gm_car/i.test(baseKey)) {
      if (/\bLS7\b/i.test(engine)) return 'GM LS7';
      if (/\b(?:LSA|LS9)\b/i.test(engine)) return 'GM LSA/LS9 PDS';
      if (/\b(?:L76|LS3|L99)\b/i.test(engine)) return 'GM LS3/L99 Car Rectangle';
      return 'GM LS Car Cathedral';
    }

    if (/gen5_gm_truck_suv/i.test(baseKey)) {
      if (/\b(?:LV3|LV1)\b/i.test(engine)) return 'GM Gen V LV3/LV1 V6';
      if (/\bL20\b/i.test(engine)) return 'GM LS Truck Cathedral';
      if (/\bL96\b/i.test(engine)) return 'GM LS Truck Rectangle';
      if (/\b(?:L83|L82|L84)\b/i.test(engine)) return 'GM Gen V 5.3 Truck';
      if (/\b(?:L86|L87)\b/i.test(engine)) return 'GM Gen V 6.2 Truck';
      if (/\bL8T\b/i.test(engine)) return 'GM Gen V L8T';
      return '';
    }

    if (/gen5_gm_car/i.test(baseKey)) {
      if (/\b(?:LT4|LT5)\b/i.test(engine)) return 'GM Gen V LT4/LT5 Supercharged';
      if (/\bLT1\b/i.test(engine)) return 'GM Gen V LT1';
    }
    return '';
  }

  function camOptionsForBaseProductKey(baseProductKey, engineValue) {
    var homepage = draftData().homepage || {};
    var map = homepage.camOptions || {};
    var hemiKey = isMoparBaseProductKey(baseProductKey) ? hemiCamOptionsKey(engineValue) : '';
    if (hemiKey && map[hemiKey]) return map[hemiKey];
    var resolvedBaseKey = baseProductKeyForEngine({ id: baseProductKey, baseProductKey: baseProductKey }, engineValue);
    var gmKey = gmCamOptionsKey(resolvedBaseKey, engineValue);
    if (gmKey && map[gmKey]) return map[gmKey];
    return map[camOptionsKeyForBaseProductKey(baseProductKey)] || map.Other || [];
  }

  function isStockDeleteCamValue(camValue) {
    return camValue === 'Stock Cam / Delete Tune Only' || camValue === 'Stock - No Cam Swap';
  }

  function isVvtDeleteOnlyCamValue(camValue) {
    return camValue === 'VVT Delete Only';
  }

  function isKnownNonAfmNonVvtGm(group, engineValue) {
    var key = String((group && (group.baseProductKey || group.id)) || '');
    return /^gen[345]_gm/i.test(key) && /\b(?:LY2|LH8|LS2)\b/i.test(String(engineValue || ''));
  }

  // Bounded exclusions for verified non-VVT engine families. Other
  // platforms retain their existing eligibility policy.
  function isVvtDeleteEligible(group, engineValue) {
    return !isKnownNonAfmNonVvtGm(group, engineValue) && !(isHemiGroup(group) && hemiCamOptionsKey(engineValue) === 'HEMI 6.1 NON-VVT');
  }

  function isHemiMdsDeleteEligible(group, engineValue) {
    var key = hemiCamOptionsKey(engineValue);
    return isHemiGroup(group) && key !== 'HEMI 6.1 NON-VVT' && key !== 'HEMI 6.2 HELLCAT';
  }

  function filterCamChoicesForGroup(group, engineValue, choices) {
    return (choices || []).filter(function (cam) {
      return !isVvtDeleteOnlyCamValue(cam) || isVvtDeleteEligible(group, engineValue);
    });
  }

  function isCustomCamSelection(camValue) {
    return /cam not listed|provide specs|custom cam|aftermarket cam/i.test(cleanPropertyValue(camValue));
  }

  function finitePositiveNumber(value) {
    var number = Number(value);
    return isFinite(number) && number > 0 ? number : null;
  }

  function calculateCamTier(specs) {
    var source = specs || {};
    var intakeDuration = finitePositiveNumber(source.intakeDuration);
    var exhaustDuration = finitePositiveNumber(source.exhaustDuration);
    var lsa = finitePositiveNumber(source.lsa);
    if (intakeDuration === null || exhaustDuration === null || lsa === null) return null;

    var averageDuration = (intakeDuration + exhaustDuration) / 2;
    var overlap = averageDuration - (2 * lsa);
    var score = averageDuration + Math.max(overlap, 0);
    var tierNumber = score < 220 ? 1 : (score < 230 ? 2 : (score < 240 ? 3 : 4));
    return {
      intakeDuration: intakeDuration,
      exhaustDuration: exhaustDuration,
      lsa: lsa,
      averageDuration: averageDuration,
      overlap: overlap,
      score: score,
      tierNumber: tierNumber,
      tier: 'Stage ' + tierNumber
    };
  }

  function compactCamNumber(value) {
    var number = Number(value);
    if (!isFinite(number)) return '';
    return String(Math.round(number * 1000) / 1000);
  }

  function buildCamSelectionLabel(camValue, specs) {
    var selection = cleanPropertyValue(camValue);
    if (!isCustomCamSelection(selection)) return selection;
    var calculation = calculateCamTier(specs);
    if (!calculation) return selection;

    var source = specs || {};
    var manufacturer = cleanPropertyValue(source.manufacturer).slice(0, 50);
    var grind = cleanPropertyValue(source.camName).slice(0, 60);
    var identity = [manufacturer, grind].filter(Boolean).join(' ') || 'Cam Not Listed';
    var parts = [
      identity,
      compactCamNumber(calculation.intakeDuration) + '/' + compactCamNumber(calculation.exhaustDuration) + ' @ .050',
      compactCamNumber(calculation.lsa) + ' LSA'
    ];
    var intakeLift = finitePositiveNumber(source.intakeLift);
    var exhaustLift = finitePositiveNumber(source.exhaustLift);
    if (intakeLift !== null || exhaustLift !== null) {
      parts.push('Lift ' + (intakeLift === null ? 'N/A' : compactCamNumber(intakeLift)) + '/' + (exhaustLift === null ? 'N/A' : compactCamNumber(exhaustLift)));
    }
    parts.push('Calculated JRJ ' + calculation.tier + ' (Score ' + compactCamNumber(calculation.score) + ')');
    return parts.join(' | ');
  }

  function camTierNumberFromSelection(camValue, contextValue) {
    var cleanCam = cleanPropertyValue(camValue);
    // Truck Norris and Chopacabra (including NSR / Gen V / LS3-labelled
    // versions) are included in JRJ Base Tune and Custom Remote Tune base
    // pricing. They must never trigger a Stage 2/3/4 cam upgrade line item.
    if (/truck\s*norris|chopacabra/i.test(cleanCam)) return 1;
    var homepage = draftData().homepage || {};
    var context = cleanPropertyValue(contextValue).toLowerCase();
    var contextKey = /hemi|mopar/.test(context) ? 'hemi' : '';
    var contextOverrides = homepage.camContextTierOverrides || {};
    var contextTier = Number(contextKey ? contextOverrides[contextKey + ':' + cleanCam] : 0);
    if (contextTier >= 1 && contextTier <= 4) return contextTier;
    var overrides = homepage.camTierOverrides || {};
    var exactTier = Number(overrides[cleanCam]);
    if (exactTier >= 1 && exactTier <= 4) return exactTier;
    var match = cleanCam.match(/stage\s*([1-4])/i);
    if (match) return Number(match[1]);
    var duration = cleanCam.match(/\b(1\d{2}|2\d{2})\s*\/\s*(1\d{2}|2\d{2})\b/);
    if (!duration) return 0;
    var averageDuration = (Number(duration[1]) + Number(duration[2])) / 2;
    return averageDuration < 220 ? 1 : (averageDuration < 230 ? 2 : (averageDuration < 240 ? 3 : 4));
  }

  function isGenVCamPricingKey(baseProductKey) {
    return /^gen5_/i.test(String(baseProductKey || ''));
  }

  // Every group whose forcedInductionEngines list is non-empty tells us
  // which base-tune products are eligible for a forced-induction selector --
  // computed from the groups data itself so this can never fall out of sync
  // with it.
  function forcedInductionEligibleBaseProductKeys() {
    var groups = draftData().groups || [];
    var keys = {};
    groups.forEach(function (g) {
      if (g.forcedInductionEngines && g.forcedInductionEngines.length && g.baseProductKey) keys[g.baseProductKey] = true;
    });
    return Object.keys(keys);
  }

  function isForcedInductionEligible(baseProductKey) {
    return forcedInductionEligibleBaseProductKeys().indexOf(baseProductKey) !== -1;
  }

  // ---------------------------------------------------------------------
  // Engine-precise platform/cam resolution.
  //
  // The homepage Guided Tune Builder and the landing-page mini tune builder
  // (jrj-mini-tune-builder.js) both have a live year/make/model/engine
  // selector, so both can classify a platform at full per-engine precision --
  // unlike the product-page builder above, which only has a static product
  // handle. These used to be two private, byte-for-byte-identical copies of
  // the same functions (one per file); they now live here once so the two
  // builders share the exact same implementation and cannot drift apart.
  // ---------------------------------------------------------------------

  function isHemiGroup(group) {
    return /hemi/i.test(String((group && group.id) || '') + ' ' + String((group && group.label) || ''));
  }

  function isGmDodAfmPlatform(group, engineValue) {
    var id = String((group && group.id) || '');
    var engine = String(engineValue || '');
    if (isKnownNonAfmNonVvtGm(group, engineValue)) return false;
    if (/\b(?:LV3|L99|L83|L82|L84|L86|L87|LT1|LT4|LY5|LMG|LC9|LH6|L76)\b/i.test(engine)) return true;
    if (id.indexOf('gen4_gm_truck_suv') !== -1 && !/L96|LY6|L92|L9H|LS3|LS7/i.test(engine)) return true;
    if (id.indexOf('gen5_gm') !== -1 && !/LV3|LV1|L8T|L20|L96/i.test(engine)) return true;
    return false;
  }

  function isVvtOnlyPlatform(group, engineValue) {
    var id = String((group && group.id) || '');
    var engine = String(engineValue || '');
    return !isGmDodAfmPlatform(group, engineValue) && !isHemiGroup(group) && (id.indexOf('gen5_gm') !== -1 || /LV3|L8T/i.test(engine));
  }

  function getCamEngineKey(group, engineValue) {
    var label = String((group && group.label) || '');
    var id = String((group && group.id) || '');
    var baseProductKey = String(baseProductKeyForEngine(group, engineValue) || id);
    if (/gen[345]_gm/i.test(id + ' ' + baseProductKey) || label.indexOf('EcoTec3') !== -1) {
      return gmCamOptionsKey(baseProductKey, engineValue) || 'GM Safe Fallback';
    }
    if (/HEMI/i.test(id + ' ' + label)) return hemiCamOptionsKey(engineValue) || 'Dodge RAM HEMI';
    return 'Other';
  }

  function camOptionsForGroup(group, engineValue) {
    var homepage = draftData().homepage || {};
    var map = homepage.camOptions || {};
    return map[getCamEngineKey(group, engineValue)] || map.Other || [];
  }

  // Mirrors getMatchedBaseProduct() from the homepage builder / mini builder.
  function resolveMatchedBaseProductForGroup(group, engineValue, camValue, baseProduct) {
    var homepage = draftData().homepage || {};
    var basics = homepage.basicProducts || {};
    if (isVvtDeleteOnlyCamValue(camValue)) return isVvtDeleteEligible(group, engineValue) ? (basics.BASIC_VVT_DELETE || baseProduct) : null;
    if (!isStockDeleteCamValue(camValue)) return baseProduct;
    if (isHemiGroup(group)) return isHemiMdsDeleteEligible(group, engineValue) ? (basics.BASIC_MDS_DELETE || baseProduct) : baseProduct;
    if (isGmDodAfmPlatform(group, engineValue)) return basics.BASIC_DOD_AFM_DELETE || baseProduct;
    if (isVvtOnlyPlatform(group, engineValue)) return basics.BASIC_VVT_DELETE || baseProduct;
    return baseProduct;
  }

  // Mirrors getSelectedCamUpgrade() from the homepage builder / mini builder.
  function resolveCamUpgradeForGroup(group, engineValue, camValue, matchedBaseProduct, yearValue, makeValue) {
    if (isStockDeleteCamValue(camValue) || isVvtDeleteOnlyCamValue(camValue)) return null;
    var camContext = [group && group.baseProductKey, group && group.id, group && group.label, engineValue].filter(Boolean).join(' ');
    var tierNumber = camTierNumberFromSelection(camValue, camContext);
    var upgradeKey = tierNumber >= 2 ? 'STAGE_' + tierNumber + '_CAM_UPGRADE' : '';
    if (!upgradeKey || (matchedBaseProduct && matchedBaseProduct.includesCamPricing)) return null;

    var homepage = draftData().homepage || {};
    var camUpgrades = homepage.camUpgradeProducts || {};
    var baseKey = baseProductKeyForEngine(group, engineValue);
    if (usesGenVAddOnProducts(baseKey, yearValue, makeValue, engineValue)) {
      return camUpgrades['GEN_V_' + upgradeKey] || camUpgrades[upgradeKey] || null;
    }
    return camUpgrades[upgradeKey] || null;
  }

  function isBasicDeleteProduct(baseProduct) {
    var homepage = draftData().homepage || {};
    var basics = homepage.basicProducts || {};
    return !!(baseProduct && baseProduct.variantId && (
      (basics.BASIC_DOD_AFM_DELETE && baseProduct.variantId === basics.BASIC_DOD_AFM_DELETE.variantId) ||
      (basics.BASIC_MDS_DELETE && baseProduct.variantId === basics.BASIC_MDS_DELETE.variantId) ||
      (basics.BASIC_VVT_DELETE && baseProduct.variantId === basics.BASIC_VVT_DELETE.variantId)
    ));
  }

  function basicDeleteSupportLabel(baseProduct) {
    var homepage = draftData().homepage || {};
    var basics = homepage.basicProducts || {};
    if (!baseProduct) return '';
    if (basics.BASIC_DOD_AFM_DELETE && baseProduct.variantId === basics.BASIC_DOD_AFM_DELETE.variantId) return 'DOD / AFM';
    if (basics.BASIC_MDS_DELETE && baseProduct.variantId === basics.BASIC_MDS_DELETE.variantId) return 'MDS';
    if (basics.BASIC_VVT_DELETE && baseProduct.variantId === basics.BASIC_VVT_DELETE.variantId) return 'VVT';
    return '';
  }

  // Single source of truth for the one add-on whose display price differs
  // from its live Shopify price when a live lookup fails.
  var ADDON_FALLBACK_PRICES_CENTS = { 'Aftermarket Torque Converter': 2499 };

  // Mirrors the homepage builder's getMatchedBaseProduct() + getSelectedCamUpgrade()
  // combined: given the platform key, its real base product, and the
  // customer's chosen cam string, returns which product should actually be
  // added to cart as the "tune" line item, plus an optional separate cam
  // upgrade line item.
  function resolveCamSelection(baseProductKey, baseProduct, camValue, yearValue, makeValue, engineValue) {
    var homepage = draftData().homepage || {};
    var basics = homepage.basicProducts || {};
    var camUpgrades = homepage.camUpgradeProducts || {};

    if (isVvtDeleteOnlyCamValue(camValue)) {
      return { baseProduct: isVvtDeleteEligible({id: baseProductKey}, engineValue) ? (basics.BASIC_VVT_DELETE || baseProduct) : null, camUpgradeItem: null };
    }

    if (isStockDeleteCamValue(camValue)) {
      if (!baseProduct || !baseProduct.includesCamPricing) {
        if (isKnownNonAfmNonVvtGm({id: baseProductKey}, engineValue)) return { baseProduct: baseProduct, camUpgradeItem: null };
        if (MDS_DELETE_BASE_KEYS.indexOf(baseProductKey) !== -1) return { baseProduct: isHemiMdsDeleteEligible({id: baseProductKey}, engineValue) ? (basics.BASIC_MDS_DELETE || baseProduct) : baseProduct, camUpgradeItem: null };
        if (DOD_AFM_DELETE_BASE_KEYS.indexOf(baseProductKey) !== -1) return { baseProduct: basics.BASIC_DOD_AFM_DELETE || baseProduct, camUpgradeItem: null };
        if (VVT_ONLY_BASE_KEYS.indexOf(baseProductKey) !== -1) return { baseProduct: basics.BASIC_VVT_DELETE || baseProduct, camUpgradeItem: null };
      }
      return { baseProduct: baseProduct, camUpgradeItem: null };
    }

    var tierNumber = camTierNumberFromSelection(camValue, baseProductKey);
    var upgradeKey = tierNumber >= 2 ? 'STAGE_' + tierNumber + '_CAM_UPGRADE' : '';

    if (!upgradeKey || (baseProduct && baseProduct.includesCamPricing)) {
      return { baseProduct: baseProduct, camUpgradeItem: null };
    }

    if (usesGenVAddOnProducts(baseProductKey, yearValue, makeValue, engineValue)) {
      return { baseProduct: baseProduct, camUpgradeItem: camUpgrades['GEN_V_' + upgradeKey] || camUpgrades[upgradeKey] || null };
    }
    return { baseProduct: baseProduct, camUpgradeItem: camUpgrades[upgradeKey] || null };
  }

  // Mirrors the homepage builder's getHomepageAddonVariantId(): resolves an
  // add-on's display name to its real variant ID, preferring the Gen V
  // variant when this platform is the Gen V truck platform.
  function addonVariantId(name, baseProductKey) {
    var homepage = draftData().homepage || {};
    var genv = homepage.genVAddonVariants || {};
    if (isGenVTruckKey(baseProductKey) && genv[name]) return genv[name];
    return (homepage.addonVariants || {})[name];
  }

  // One shared, atomic /cart/add.js call + one shared error-handling contract.
  // items: Shopify cart items array (already resolved to real variant ids).
  // Returns a Promise that resolves on success (after either opening the
  // cart drawer or redirecting to /cart) and rejects with the Error on
  // failure — caller decides how to
  // surface the failure (this module never shows UI itself, so both builders
  // can keep their own on-page error element).
  function renderAddedCart(payload) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      return Promise.reject(new Error('Cart drawer is not available.'));
    }

    var shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    var sectionsUrl = shopRoot + '?sections=cart-drawer,cart-icon-bubble&sections_url=' + encodeURIComponent(window.location.pathname);

    return fetch(sectionsUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Could not refresh the cart drawer.');
      return res.json();
    }).then(function (sections) {
      var state = Object.assign({}, payload || {});
      state.sections = sections;
      state.id = state.id || (payload && payload.items && payload.items[0] && payload.items[0].id) || Date.now();
      drawer.renderContents(state);
      return state;
    });
  }

  function addItemsToCart(items, options) {
    if (!items || !items.length) return Promise.reject(new Error('No items to add.'));
    var requirePortalSnapshot = !options || options.requirePortalSnapshot !== false;
    if (requirePortalSnapshot) {
      var requestValidation = validateRequestedCartItems(items);
      if (!requestValidation.ok) {
        console.error('JRJ Tune Cart: blocked incomplete build before cart submission', {
          errors: requestValidation.errors,
          items: safeDiagnosticItems(items)
        });
        return Promise.reject(new Error('Complete and confirm every required vehicle and tune selection before retrying.'));
      }
    }
    var responsePayload = null;
    return fetchWithTimeout('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }, 8000).then(function (res) {
      if (!res.ok) {
        return res.text().catch(function () { return ''; }).then(function (bodyText) {
          console.warn('JRJ Tune Cart: /cart/add.js failed', { status: res.status, body: bodyText, items: safeDiagnosticItems(items) });
          throw new Error('Shopify cart add failed with status ' + res.status);
        });
      }
      return res.json().catch(function () { throw new Error('Shopify returned an unreadable cart response.'); });
    }).then(function (payload) {
      responsePayload = payload;
      if (requirePortalSnapshot) {
        var verification = verifyReturnedCartItems(payload, items);
        if (!verification.ok) {
          console.error('JRJ Tune Cart: Shopify returned a tune line without the confirmed build snapshot', {
            errors: verification.errors,
            requested: safeDiagnosticItems(items),
            returned: safeDiagnosticItems(verification.items)
          });
          return rollbackReturnedItems(payload).then(function () {
            throw new Error('Shopify did not save the confirmed vehicle/build details. Nothing from this attempt was kept; please retry.');
          });
        }
      }
      if (options && options.openCartDrawer) {
        return renderAddedCart(payload).catch(function (error) {
          console.warn('JRJ Tune Cart: cart drawer refresh failed; opening cart page instead.', error);
          window.location.assign('/cart');
          return payload;
        });
      }
      if (!options || options.redirect !== false) window.location.assign('/cart');
      return payload;
    }).catch(function (error) {
      if (!responsePayload && /unreadable cart response/i.test(error && error.message || '')) {
        return rollbackByLinkedTuneKeys(items).then(function () { throw error; });
      }
      throw error;
    });
  }

  return {
    fetchWithTimeout: fetchWithTimeout,
    activeCurrency: activeCurrency,
    formatPrice: formatPrice,
    invalidateCatalogCache: invalidateCatalogCache,
    PORTAL_PURCHASE_PROPERTY_NAMES: PORTAL_PURCHASE_PROPERTY_NAMES,
    REQUIRED_PORTAL_SELECTION_NAMES: REQUIRED_PORTAL_SELECTION_NAMES,
    buildPortalPurchaseProperties: buildPortalPurchaseProperties,
    validatePortalPurchaseProperties: validatePortalPurchaseProperties,
    verifyReturnedCartItems: verifyReturnedCartItems,
    normalizeModsValue: normalizeModsValue,
    createLinkedTuneKey: createLinkedTuneKey,
    buildLinkedTuneProperties: buildLinkedTuneProperties,
    markPrimaryTuneProperties: markPrimaryTuneProperties,
    resolveConfiguredVariantByOptions: resolveConfiguredVariantByOptions,
    getVariantFromHandle: getVariantFromHandle,
    getVariantById: getVariantById,
    resolvePricedItem: resolvePricedItem,
    resolveForcedInductionAddon: resolveForcedInductionAddon,
    addItemsToCart: addItemsToCart,
    bundleDiscountApplies: bundleDiscountApplies,
    isMoparMake: isMoparMake,
    boostBrandsForMake: boostBrandsForMake,
    FORCED_INDUCTION_BLOWER_OPTIONS: FORCED_INDUCTION_BLOWER_OPTIONS,
    GM_BOOST_BRANDS: GM_BOOST_BRANDS,
    MOPAR_BOOST_BRANDS: MOPAR_BOOST_BRANDS,
    CAM_BLOWER_BUNDLE_DISCOUNT: CAM_BLOWER_BUNDLE_DISCOUNT,
    FORCED_INDUCTION_ADDON_HANDLES: FORCED_INDUCTION_ADDON_HANDLES,
    baseProductForKey: baseProductForKey,
    vehicleYears: vehicleYears,
    vehicleMakes: vehicleMakes,
    vehicleModels: vehicleModels,
    vehicleEngines: vehicleEngines,
    vehicleSelectionMatches: vehicleSelectionMatches,
    resolveVehicleSelection: resolveVehicleSelection,
    engineRuleAppliesToVehicle: engineRuleAppliesToVehicle,
    modelAllowedForGroupYear: modelAllowedForGroupYear,
    engineApplicationFor: engineApplicationFor,
    baseProductKeyForEngine: baseProductKeyForEngine,
    findBaseProductKeyByHandle: findBaseProductKeyByHandle,
    configuredCustomTunePriceRange: configuredCustomTunePriceRange,
    resolveCustomTunePriceRange: resolveCustomTunePriceRange,
    formatStartingPriceRange: formatStartingPriceRange,
    isGenVTruckKey: isGenVTruckKey,
    isRecognizedGmMake: isRecognizedGmMake,
    isGmAddOnContext: isGmAddOnContext,
    usesGenVAddOnProducts: usesGenVAddOnProducts,
    addonProductConfig: addonProductConfig,
    resolveAddonProduct: resolveAddonProduct,
    isMoparBaseProductKey: isMoparBaseProductKey,
    camOptionsForBaseProductKey: camOptionsForBaseProductKey,
    isStockDeleteCamValue: isStockDeleteCamValue,
    isVvtDeleteOnlyCamValue: isVvtDeleteOnlyCamValue,
    isCustomCamSelection: isCustomCamSelection,
    calculateCamTier: calculateCamTier,
    buildCamSelectionLabel: buildCamSelectionLabel,
    camTierNumberFromSelection: camTierNumberFromSelection,
    isGenVCamPricingKey: isGenVCamPricingKey,
    isForcedInductionEligible: isForcedInductionEligible,
    resolveCamSelection: resolveCamSelection,
    addonVariantId: addonVariantId,
    isHemiGroup: isHemiGroup,
    isGmDodAfmPlatform: isGmDodAfmPlatform,
    isVvtOnlyPlatform: isVvtOnlyPlatform,
    getCamEngineKey: getCamEngineKey,
    camOptionsForGroup: camOptionsForGroup,
    isVvtDeleteEligible: isVvtDeleteEligible,
    filterCamChoicesForGroup: filterCamChoicesForGroup,
    resolveMatchedBaseProductForGroup: resolveMatchedBaseProductForGroup,
    resolveCamUpgradeForGroup: resolveCamUpgradeForGroup,
    isBasicDeleteProduct: isBasicDeleteProduct,
    basicDeleteSupportLabel: basicDeleteSupportLabel,
    ADDON_FALLBACK_PRICES_CENTS: ADDON_FALLBACK_PRICES_CENTS
  };
})();
